// Fichier: src/pages/StudentEvolutions.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, query, where } from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import 'moment/locale/fr';

const TRACK_LABELS = {
  leisure: 'Loisir',
  foundation: 'Foundation',
  performance: 'Performance',
  competition: 'Compétition',
  freestyle: 'Freestyle',
  intensive: 'Intensif',
};

const TRACK_COLORS = {
  leisure: 'default',
  foundation: 'info',
  performance: 'primary',
  competition: 'warning',
  freestyle: 'secondary',
  intensive: 'success',
};

const StudentEvolutions = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [trackFilter, setTrackFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const getFullName = (user) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        fullName: getFullName({ id: d.id, ...d.data() }),
      }));
      setStudents(users);

      const profilesSnapshot = await getDocs(collection(db, 'student_evolution_profiles'));
      const profilesData = profilesSnapshot.docs.map((d) => {
        const data = d.data();
        const student = users.find((u) => u.id === (data.studentId || d.id));
        return {
          id: d.id,
          ...data,
          studentId: data.studentId || d.id,
          studentName: data.studentName || student?.fullName || 'Élève inconnu',
          studentEmail: student?.email || '',
          updatedAtDate: data.updatedAt?.toDate?.() || null,
          createdAtDate: data.createdAt?.toDate?.() || null,
        };
      });

      setProfiles(profilesData);
    } catch (error) {
      console.error('Erreur chargement suivis individuels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!profileToDelete) return;
    try {
      await deleteDoc(doc(db, 'student_evolution_profiles', profileToDelete.id));
      setProfiles((prev) => prev.filter((p) => p.id !== profileToDelete.id));
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    } catch (error) {
      console.error('Erreur suppression profil évolution:', error);
    }
  };

  const averageProgress = (profile) => {
    const goals = profile.seasonGoals || [];
    if (!goals.length) return 0;
    const total = goals.reduce((sum, g) => sum + (Number(g.progress) || 0), 0);
    return Math.round(total / goals.length);
  };

  const filteredProfiles = profiles.filter((profile) => {
    const s = search.toLowerCase().trim();
    const matchesSearch = !s ||
      profile.studentName?.toLowerCase().includes(s) ||
      profile.studentEmail?.toLowerCase().includes(s) ||
      profile.season?.toLowerCase().includes(s);
    const matchesTrack = trackFilter === 'all' || profile.track === trackFilter;
    return matchesSearch && matchesTrack;
  });

  const columns = [
    {
      field: 'studentName',
      headerName: 'Élève',
      width: 230,
      renderCell: (params) => (
        <Button
          onClick={() => navigate(`/student-evolutions/${params.row.id}`)}
          sx={{ textTransform: 'none', color: 'primary.main', justifyContent: 'flex-start', width: '100%' }}
        >
          {params.value}
        </Button>
      ),
    },
    { field: 'studentEmail', headerName: 'Email', width: 230 },
    {
      field: 'season',
      headerName: 'Saison',
      width: 130,
      valueGetter: (value, row) => row?.season || '2026-2027',
    },
    {
      field: 'track',
      headerName: 'Track',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={TRACK_LABELS[params.value] || 'Non défini'}
          color={TRACK_COLORS[params.value] || 'default'}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'goalsCount',
      headerName: 'Objectifs',
      width: 120,
      valueGetter: (value, row) => (row?.seasonGoals || []).length,
    },
    {
      field: 'progress',
      headerName: 'Progression',
      width: 180,
      renderCell: (params) => {
        const progress = averageProgress(params.row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
              <Box
                sx={{
                  height: 8,
                  width: `${progress}%`,
                  bgcolor: progress >= 100 ? 'success.main' : '#7C3AED',
                  borderRadius: 1,
                }}
              />
            </Box>
            <Typography variant="body2">{progress}%</Typography>
          </Box>
        );
      },
    },
    {
      field: 'updatedAtDate',
      headerName: 'Dernière MAJ',
      width: 160,
      valueGetter: (value, row) =>
  row?.updatedAtDate ? moment(row.updatedAtDate).format('DD/MM/YYYY') : '—',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 230,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => navigate(`/student-evolutions/edit/${params.row.id}`)}>
            Modifier
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              setProfileToDelete(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            Supprimer
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Suivi individuel</Typography>
          <Typography color="text.secondary">Plans d’évolution personnalisés des élèves</Typography>
        </Box>
        <Button
          variant="contained"
          component={Link}
          to="/student-evolutions/new"
          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
        >
          Créer un suivi
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Rechercher un élève"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          label="Track"
          value={trackFilter}
          onChange={(e) => setTrackFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Tous</MenuItem>
          {Object.entries(TRACK_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ height: 650, width: '100%' }}>
        <DataGrid
          rows={filteredProfiles}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{ '& .MuiDataGrid-cell:hover': { backgroundColor: 'rgba(124, 58, 237, 0.04)' } }}
        />
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer le suivi individuel de "{profileToDelete?.studentName}" ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDelete} color="error" autoFocus>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentEvolutions;
